import { type Board, type Clip } from '@air/api/types';

import { type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';

export type PublicShowcaseListingBlockCategory = 'assets' | 'boards' | 'files';

/**
 * The wire's block categories name the product concept ('assets') while the gallery data keys the
 * fetch ('clips') — this maps between the two. Exported because the blocks host reads the same
 * sections to decide whether the stack renders anything at all.
 */
export const getPublicShowcaseListingSection = (
  data: UseGalleryViewItemsReturn<Board, Clip>['data'],
  category: PublicShowcaseListingBlockCategory,
) => {
  if (category === 'boards') {
    return data.boards;
  }

  if (category === 'assets') {
    return data.clips;
  }

  return data.files;
};

type NestedChipHostCandidate = {
  hidden?: boolean;
  type: string;
};

/**
 * The folded chip sits on the first listing that is actually on the page and still has items.
 * Hidden blocks are not drawn, and a category the author left off the page is not a host even
 * when the fetch for that category has rows. Boards come first when they are visible; once Expand
 * clears their total, the next visible listing takes the chip. While a listing's count is still
 * loading, it holds the chip so parallel fetches finishing out of order cannot park it downstream.
 */
export const getNestedChipHostCategory = (
  blocks: readonly NestedChipHostCandidate[],
  data: UseGalleryViewItemsReturn<Board, Clip>['data'],
): PublicShowcaseListingBlockCategory | undefined => {
  for (const block of blocks) {
    if (block.hidden) {
      continue;
    }

    if (block.type !== 'assets' && block.type !== 'boards' && block.type !== 'files') {
      continue;
    }

    const section = getPublicShowcaseListingSection(data, block.type);

    if (section.total > 0) {
      return block.type;
    }

    // Parallel fetches can finish out of order. Until this listing's count is known, keep the chip
    // here so a faster later category is not mistaken for the host (or for "no listings left").
    if (section.isLoading) {
      return block.type;
    }
  }

  return undefined;
};
